import React, { useState } from 'react';
import {
    DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Spinner, Stack,
    IconButton, Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton, MessageBar,
    MessageBarType, Toggle, Text, TextField, ChoiceGroup,
} from '@fluentui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useGetOrganizationQuery } from '../../store/endpoints/OrganizationsEndpoints';
import {
    useCreatePromotionMutation, useDeletePromotionMutation, useGetPromotionsQuery,
    useUpdatePromotionMutation,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import NumberField from '../Inputs/NumberField';
import PromoPricingFields from './PromoPricingFields';
import {
    IPromotion, PROMO_MARKUP_PERCENT, describePromoPricing, isDiscountPromo,
} from '../../models/ProductPromo';

interface IProps {
    /** Storefront id, or undefined for every promotion in the catalog. */
    storefront?: string;
}

/** The datetime-local input has no timezone of its own, so it works in local wall-clock. */
const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatWindow = (promotion: IPromotion) => {
    const starts = promotion.startsAt ? new Date(promotion.startsAt).toLocaleDateString() : 'Immediately';
    const ends = promotion.endsAt ? new Date(promotion.endsAt).toLocaleDateString() : 'No end';
    return `${starts} → ${ends}`;
};

/**
 * Whether a campaign is pricing anything right now.
 *
 * Mirrors the API's Promotion.IsActiveFor, minus the storefront scope (the list is already filtered by
 * storefront). Half-open window: a promotion ending "Sept 1" stops at the first instant of Sept 1.
 * Shown rather than inferred from dates alone because a paused campaign is inside its window and still
 * prices nothing, which would otherwise read as a bug.
 */
const statusOf = (promotion: IPromotion): { label: string, color: string } => {
    const now = Date.now();
    if (promotion.active === false) return { label: 'Paused', color: '#8a8886' };
    if (promotion.startsAt && now < new Date(promotion.startsAt).getTime())
        return { label: 'Scheduled', color: '#0078d4' };
    if (promotion.endsAt && now >= new Date(promotion.endsAt).getTime())
        return { label: 'Ended', color: '#8a8886' };
    return { label: 'Running', color: '#107c10' };
};

const StorefrontPromotionsAdminTable: React.FC<IProps> = (props) => {
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', { skip: !selectedOrganization });
    const catalog = organization.data?.defaultCatalog;

    const promotions = useGetPromotionsQuery(
        { catalog: catalog ?? '', storefront: props.storefront, includeInactive: true },
        { skip: !catalog });
    const [updatePromotion, updateState] = useUpdatePromotionMutation();
    const [deletePromotion] = useDeletePromotionMutation();
    const [createPromotion, createState] = useCreatePromotionMutation();

    const [editorOpen, setEditorOpen] = useState<boolean>(false);
    const [draft, setDraft] = useState<IPromotion>({});
    const [allStorefronts, setAllStorefronts] = useState<boolean>(false);
    const [pendingDelete, setPendingDelete] = useState<IPromotion | undefined>();
    const [error, setError] = useState<string | undefined>();

    // The same dialog both ways: a campaign is one record whether it is being created or corrected, and
    // two forms for it is how the two drift apart.
    const editing = !!draft.id;
    const saving = createState.isLoading || updateState.isLoading;

    const openCreate = () => {
        setDraft({ markupType: PROMO_MARKUP_PERCENT, active: true });
        // Scoped to the storefront being edited, matching where the button lives.
        setAllStorefronts(!props.storefront);
        setError(undefined);
        setEditorOpen(true);
    };

    const openEdit = (promotion: IPromotion) => {
        setDraft({ ...promotion });
        setAllStorefronts(!(promotion.storefronts && promotion.storefronts.length > 0));
        setError(undefined);
        setEditorOpen(true);
    };

    // A campaign created here starts with NO products. That is the point of this path: set the
    // promotion up first, then add products to it from the Products tab. Editing one changes nothing
    // else — the products carry a link rather than a copy, so the new values are live immediately.
    const save = async () => {
        if (!catalog) return;
        setError(undefined);
        // Null as well as undefined: a campaign stored without a markup comes back from the API as
        // null, and that is exactly the record this dialog exists to correct.
        if (draft.markup === undefined || draft.markup === null) {
            setError(isDiscountPromo(draft.markupType)
                ? 'Enter the discount percentage.'
                : 'Enter the promotional markup.');
            return;
        }
        if (isDiscountPromo(draft.markupType) && (draft.markup <= 0 || draft.markup >= 100)) {
            setError('The discount must be greater than 0 and less than 100 percent.');
            return;
        }
        // Empty means every storefront, which is what the API reads a cleared list as.
        const request: IPromotion = {
            ...draft,
            storefronts: allStorefronts ? [] : (props.storefront ? [props.storefront] : draft.storefronts),
        };
        try {
            if (editing) await updatePromotion({ catalog, id: draft.id!, request }).unwrap();
            else await createPromotion({ catalog, request }).unwrap();
            setEditorOpen(false);
            setDraft({});
        } catch {
            setError(editing
                ? 'Could not save the promotion. Check the name, markup and dates.'
                : 'Could not create the promotion. Check the name, markup and dates.');
        }
    };

    const togglePaused = async (promotion: IPromotion) => {
        if (!catalog || !promotion.id) return;
        setError(undefined);
        try {
            // Pausing edits the campaign only. Its products are untouched, because they carry the link
            // rather than a copy of these values — which is the whole point of the campaign record.
            await updatePromotion({
                catalog, id: promotion.id,
                request: { ...promotion, active: !(promotion.active ?? true) },
            }).unwrap();
        } catch {
            setError('Could not update the promotion.');
        }
    };

    const confirmDelete = async () => {
        if (!catalog || !pendingDelete?.id) return;
        setError(undefined);
        try {
            await deletePromotion({ catalog, id: pendingDelete.id }).unwrap();
            setPendingDelete(undefined);
        } catch {
            setError('Could not remove the promotion.');
        }
    };

    const columns: IColumn[] = [
        {
            key: 'name', name: 'Promotion', fieldName: 'name', minWidth: 160, maxWidth: 280, isResizable: true,
            onRender: (promotion: IPromotion) => (
                <Stack>
                    <Text variant='smallPlus' style={{ fontWeight: 600 }}>{promotion.name ?? 'Untitled'}</Text>
                    {promotion.description && (
                        <Text variant='small' style={{ color: '#605e5c' }}>{promotion.description}</Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'status', name: 'Status', minWidth: 80, maxWidth: 100,
            onRender: (promotion: IPromotion) => {
                const status = statusOf(promotion);
                return <Text variant='small' style={{ color: status.color, fontWeight: 600 }}>{status.label}</Text>;
            },
        },
        {
            key: 'window', name: 'Window', minWidth: 170, maxWidth: 220,
            onRender: (promotion: IPromotion) => <Text variant='small'>{formatWindow(promotion)}</Text>,
        },
        {
            // Says WHICH quantity the number is. "20%" alone reads as a discount whether it is a margin
            // or a discount, and those are different prices.
            key: 'markup', name: 'Pricing', minWidth: 100, maxWidth: 130,
            onRender: (promotion: IPromotion) => (
                <Text variant='small'>{describePromoPricing(promotion.markupType, promotion.markup)}</Text>
            ),
        },
        {
            key: 'products', name: 'Products', minWidth: 70, maxWidth: 90,
            onRender: (promotion: IPromotion) => <Text variant='small'>{promotion.productCount ?? 0}</Text>,
        },
        {
            key: 'allocation', name: 'Allocation', minWidth: 90, maxWidth: 130,
            onRender: (promotion: IPromotion) => (
                // Per product, not shared across the campaign — labelled explicitly because the opposite
                // reading ("40 units total") is the natural one and would be wrong.
                <Text variant='small'>
                    {promotion.allocationQuantity ? `${promotion.allocationQuantity} / product` : 'Unlimited'}
                </Text>
            ),
        },
        {
            key: 'storefronts', name: 'Storefronts', minWidth: 100, maxWidth: 160,
            onRender: (promotion: IPromotion) => (
                <Text variant='small'>
                    {promotion.storefronts?.length ? promotion.storefronts.join(', ') : 'All storefronts'}
                </Text>
            ),
        },
        {
            key: 'actions', name: '', minWidth: 130, maxWidth: 130,
            onRender: (promotion: IPromotion) => (
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <IconButton
                        iconProps={{ iconName: 'Edit' }}
                        title='Edit this promotion'
                        ariaLabel='Edit promotion'
                        onClick={() => openEdit(promotion)}
                    />
                    <Toggle
                        checked={promotion.active ?? true}
                        onChange={() => togglePaused(promotion)}
                        styles={{ root: { marginBottom: 0 } }}
                        ariaLabel='Pause or resume this promotion'
                    />
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title='Remove promotion'
                        ariaLabel='Remove promotion'
                        onClick={() => setPendingDelete(promotion)}
                    />
                </Stack>
            ),
        },
    ];

    if (promotions.isLoading) return <Spinner label='Loading promotions…' />;

    const items = promotions.data ?? [];

    return (
        <Stack tokens={{ childrenGap: '1em', padding: '1em 0' }}>
            {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(undefined)}>{error}</MessageBar>}

            <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                <PrimaryButton
                    text='New Promotion'
                    iconProps={{ iconName: 'Add' }}
                    onClick={openCreate}
                />
            </Stack>

            {items.length === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    No promotions yet. Select products on the Products tab and use Create Promotion to start one.
                </MessageBar>
            ) : (
                <DetailsList
                    items={items}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    compact
                />
            )}

            <Dialog
                hidden={!editorOpen}
                onDismiss={() => setEditorOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: editing ? 'Edit Promotion' : 'New Promotion',
                    // Editing says what an edit reaches, because the answer is not obvious: the products
                    // hold a link rather than a copy, so every one of them reprices the moment this saves.
                    subText: editing
                        ? `Applies immediately to the ${draft.productCount ?? 0} product(s) on this promotion.`
                        : 'Creates an empty promotion. Add products to it from the Products tab.',
                }}
                minWidth={480}
            >
                <Stack tokens={{ childrenGap: '0.6em' }}>
                    <TextField
                        label='Name'
                        required
                        description='Shown to the shopper as a badge on the product.'
                        value={draft.name ?? ''}
                        onChange={(_, value) => setDraft({ ...draft, name: value })}
                        placeholder='e.g. Spring Clearance'
                    />
                    <TextField
                        label='Description'
                        description='Internal only. Not shown to the shopper.'
                        value={draft.description ?? ''}
                        onChange={(_, value) => setDraft({ ...draft, description: value })}
                    />
                    <PromoPricingFields
                        markupType={draft.markupType}
                        markup={draft.markup}
                        onChange={(markupType, markup) => setDraft({ ...draft, markupType, markup })}
                    />
                    <NumberField
                        label='Allocation (per product)'
                        toolTip='Units offered at the promo price on EACH product. Leave blank for unlimited.'
                        value={draft.allocationQuantity}
                        onChange={(value) => setDraft({ ...draft, allocationQuantity: value })}
                    />
                    <TextField
                        label='Starts'
                        type='datetime-local'
                        description='Leave blank to start immediately.'
                        value={toLocalInput(draft.startsAt)}
                        onChange={(_, value) => setDraft({
                            ...draft,
                            startsAt: value ? new Date(value).toISOString() : undefined,
                        })}
                    />
                    <TextField
                        label='Ends'
                        type='datetime-local'
                        description='Leave blank for no end date.'
                        value={toLocalInput(draft.endsAt)}
                        onChange={(_, value) => setDraft({
                            ...draft,
                            endsAt: value ? new Date(value).toISOString() : undefined,
                        })}
                    />
                    {props.storefront && (
                        <ChoiceGroup
                            label='Where this promotion applies'
                            selectedKey={allStorefronts ? 'all' : 'this'}
                            options={[
                                { key: 'this', text: 'This storefront only' },
                                { key: 'all', text: 'Every storefront' },
                            ]}
                            onChange={(_, option) => setAllStorefronts(option?.key === 'all')}
                        />
                    )}
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text={saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
                        disabled={!draft.name || saving}
                        onClick={save}
                    />
                    <DefaultButton text='Cancel' onClick={() => setEditorOpen(false)} disabled={saving} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!pendingDelete}
                onDismiss={() => setPendingDelete(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Remove promotion',
                    // Says what actually happens: the products keep their link, the campaign simply stops
                    // resolving, so nothing has to be unpicked and prices return to normal on their own.
                    subText: pendingDelete
                        ? `Remove "${pendingDelete.name ?? 'Untitled'}"? The ${pendingDelete.productCount ?? 0} product(s) on it go back to their normal price. They are not otherwise changed.`
                        : undefined,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text='Remove' onClick={confirmDelete} />
                    <DefaultButton text='Cancel' onClick={() => setPendingDelete(undefined)} />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
};

export default StorefrontPromotionsAdminTable;
